/**
 * Custom Size Calculator Component
 * An interactive size recommendation tool for apparel
 */

class SizeCalculator extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 1;
    this.totalSteps = 4;
    this.selections = {
      fitType: null,
      physique: null,
      preference: null,
      currentSize: null
    };

    // Size order for calculations (input options)
    this.inputSizes = ['S', 'M', 'L', 'XL', '2XL'];
    // Valid output sizes (only S, M, L, XL)
    this.outputSizes = ['S', 'M', 'L', 'XL'];

    this.bindEvents();
  }

  connectedCallback() {
    this.modal = this.querySelector('.size-calculator-modal');
    // Query from modal to ensure references work even after moving to body
    if (this.modal) {
      this.progressSteps = this.modal.querySelectorAll('.progress-step');
      this.steps = this.modal.querySelectorAll('.size-calculator-step');
      this.resultSection = this.modal.querySelector('.size-calculator-result');
      this.navBtns = this.modal.querySelector('.size-calculator-nav');
      this.backBtn = this.modal.querySelector('.nav-btn.back');
      this.nextBtn = this.modal.querySelector('.nav-btn.next');
      this.calculateBtn = this.modal.querySelector('.nav-btn.calculate');
    }

    // Find size chart modal
    this.sizeChartModal = this.querySelector('.size-chart-modal') || document.querySelector('.size-chart-modal');

    this.updateUI();
  }

  disconnectedCallback() {
    // Clean up: remove modal from body if it was moved there
    if (this.modal && this.modal.parentElement === document.body) {
      this.modal.remove();
    }
    if (this.sizeChartModal && this.sizeChartModal.parentElement === document.body) {
      this.sizeChartModal.remove();
    }
    // Remove body class if still present
    document.body.classList.remove('size-calculator-open');
  }

  bindEvents() {
    // Open modal
    document.addEventListener('click', (e) => {
      const calcTrigger = e.target.closest('.size-calculator-trigger');
      if (calcTrigger) {
        const parentCalculator = calcTrigger.closest('size-calculator');
        if (parentCalculator === this || (!parentCalculator && document.querySelector('size-calculator') === this) || (this.sizeChartModal && this.sizeChartModal.contains(calcTrigger))) {
          e.preventDefault();
          if (this.sizeChartModal && this.sizeChartModal.hasAttribute('open')) {
            this.closeSizeChart();
          }
          this.openModal();
        }
      }

      const chartTrigger = e.target.closest('.size-chart-trigger');
      if (chartTrigger) {
        const parentCalculator = chartTrigger.closest('size-calculator');
        if (parentCalculator === this || (!parentCalculator && document.querySelector('size-calculator') === this) || (this.modal && this.modal.contains(chartTrigger))) {
          e.preventDefault();
          if (this.modal && this.modal.hasAttribute('open')) {
            this.closeModal();
          }
          this.openSizeChart();
        }
      }
    });

    // Close modal - clicking close button, overlay, or anywhere outside the container
    this.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('.size-calculator-close');
      const overlay = e.target.closest('.size-calculator-overlay');
      const modal = e.target.closest('.size-calculator-modal');
      const container = e.target.closest('.size-calculator-container');

      // Close if clicking close button, overlay, or modal background (but not the container content)
      if (closeBtn || overlay || (modal && !container)) {
        this.closeModal();
      }
    });

    // Close size chart modal handled globally
    document.addEventListener('click', (e) => {
      if (!this.sizeChartModal || !this.sizeChartModal.hasAttribute('open')) return;

      const closeBtn = e.target.closest('.size-chart-close');
      const overlay = e.target.closest('.size-chart-overlay');
      const drawer = e.target.closest('.size-chart-drawer');
      const modal = e.target.closest('.size-chart-modal');

      if (closeBtn || overlay || (modal && !drawer)) {
        this.closeSizeChart();
      }
    });

    // Option selection
    this.addEventListener('click', (e) => {
      const optionCard = e.target.closest('.option-card');
      const sizeOption = e.target.closest('.size-option');

      if (optionCard) {
        this.selectOption(optionCard);
      }
      if (sizeOption) {
        this.selectSize(sizeOption);
      }
    });

    // Navigation
    this.addEventListener('click', (e) => {
      if (e.target.closest('.nav-btn.back')) {
        this.prevStep();
      }
      if (e.target.closest('.nav-btn.next')) {
        this.nextStep();
      }
      if (e.target.closest('.nav-btn.calculate')) {
        this.calculateSize();
      }
    });

    // Result actions
    this.addEventListener('click', (e) => {
      if (e.target.closest('.btn-apply-size')) {
        this.applySize();
      }
      if (e.target.closest('.btn-recalculate')) {
        this.reset();
      }
      if (e.target.closest('.alt-size')) {
        const altSize = e.target.closest('.alt-size');
        this.applySpecificSize(altSize.dataset.size);
      }
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.modal?.hasAttribute('open')) {
          this.closeModal();
        }
        if (this.sizeChartModal?.hasAttribute('open')) {
          this.closeSizeChart();
        }
      }
    });

    // Unit Switch
    document.addEventListener('change', (e) => {
      if (e.target.id === 'unit-toggle') {
        const isUnitInches = e.target.checked;
        const labels = e.target.closest('.unit-switch-wrapper').querySelectorAll('.unit-label');
        labels[0].classList.toggle('active', !isUnitInches);
        labels[1].classList.toggle('active', isUnitInches);

        if (this.currentChartData) {
          this.renderSizeChartTable(isUnitInches);
        }
      }
    });
  }

  openModal() {
    if (!this.modal) return;

    // Move modal to body to prevent clipping by parent containers
    if (this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
      // Re-attach event listeners to modal since it's now in body
      this.attachModalEvents();
    }

    this.modal.setAttribute('open', '');
    document.body.classList.add('size-calculator-open');

    // Focus first interactive element
    setTimeout(() => {
      const firstOption = this.modal.querySelector('.size-calculator-step.active .option-card, .size-calculator-step.active .size-option');
      if (firstOption) firstOption.focus();
    }, 100);
  }

  openSizeChart() {
    if (!this.sizeChartModal) return;

    if (this.sizeChartModal.parentElement !== document.body) {
      document.body.appendChild(this.sizeChartModal);
    }

    // Wait a tick for display:block to apply before adding transform for animation
    this.sizeChartModal.setAttribute('open', '');
    document.body.classList.add('size-calculator-open');

    // Load size chart data if not already loaded
    if (!this.sizeChartLoaded) {
      this.loadSizeChartData();
    }
  }

  async loadSizeChartData() {
    const sizeChartContent = this.sizeChartModal.querySelector('.size-chart-content');
    if (!sizeChartContent) return;

    const jsonUrl = sizeChartContent.dataset.jsonUrl;
    const fitType = sizeChartContent.dataset.fitType;
    const table = sizeChartContent.querySelector('.size-chart-table');
    const loadingEl = sizeChartContent.querySelector('.size-chart-loading');
    const errorEl = sizeChartContent.querySelector('.size-chart-error');
    const titleEl = sizeChartContent.querySelector('.size-chart-title');

    if (!jsonUrl || !fitType) return;

    try {
      const response = await fetch(jsonUrl);
      if (!response.ok) throw new Error('Failed to load JSON');

      const data = await response.json();
      const chartData = data[fitType];

      if (!chartData) {
        throw new Error('Fit type not found in JSON');
      }

      this.currentChartData = chartData;

      // Render Table
      const unitToggle = this.sizeChartModal.querySelector('#unit-toggle');
      const isUnitInches = unitToggle ? unitToggle.checked : false;
      this.renderSizeChartTable(isUnitInches);

      // Show table, hide loading
      if (loadingEl) loadingEl.style.display = 'none';
      table.style.display = 'table';
      this.sizeChartLoaded = true;

    } catch (error) {
      console.error('Error loading size chart:', error);
      if (loadingEl) loadingEl.style.display = 'none';
      if (errorEl) errorEl.style.display = 'block';
    }
  }

  renderSizeChartTable(isUnitInches) {
    if (!this.currentChartData || !this.sizeChartModal) return;
    const table = this.sizeChartModal.querySelector('.size-chart-table');
    if (!table) return;

    // Populate Table Headers
    const thead = table.querySelector('thead');
    let thContent = '<tr>';
    this.currentChartData.headers.forEach(header => {
      thContent += `<th>${header}</th>`;
    });
    thContent += '</tr>';
    thead.innerHTML = thContent;

    // Populate Table Rows
    const tbody = table.querySelector('tbody');
    let trContent = '';
    this.currentChartData.rows.forEach(row => {
      trContent += '<tr>';
      row.forEach(cell => {
        let displayCell = cell;
        if (isUnitInches && typeof cell === 'string' && cell.includes('cm')) {
          displayCell = cell.replace(/\d+(?:\.\d+)?/g, (match) => {
            return Math.round((parseFloat(match) / 2.54) * 2) / 2;
          }).replace('cm', 'in');
        }
        trContent += `<td>${displayCell}</td>`;
      });
      trContent += '</tr>';
    });
    tbody.innerHTML = trContent;
  }

  closeSizeChart() {
    if (!this.sizeChartModal) return;
    this.sizeChartModal.removeAttribute('open');
    document.body.classList.remove('size-calculator-open');
  }

  attachModalEvents() {
    // Attach event listeners directly to modal for clicks inside it
    if (!this.modal) return;

    // Close modal handlers
    this.modal.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('.size-calculator-close');
      const overlay = e.target.closest('.size-calculator-overlay');
      const container = e.target.closest('.size-calculator-container');

      // Close if clicking close button, overlay, or modal background (but not the container content)
      if (closeBtn || overlay || (e.target === this.modal && !container)) {
        this.closeModal();
      }
    });

    // Option selection
    this.modal.addEventListener('click', (e) => {
      const optionCard = e.target.closest('.option-card');
      const sizeOption = e.target.closest('.size-option');

      if (optionCard) {
        this.selectOption(optionCard);
      }
      if (sizeOption) {
        this.selectSize(sizeOption);
      }
    });

    // Navigation
    this.modal.addEventListener('click', (e) => {
      if (e.target.closest('.nav-btn.back')) {
        this.prevStep();
      }
      if (e.target.closest('.nav-btn.next')) {
        this.nextStep();
      }
      if (e.target.closest('.nav-btn.calculate')) {
        this.calculateSize();
      }
    });

    // Result actions
    this.modal.addEventListener('click', (e) => {
      if (e.target.closest('.btn-apply-size')) {
        this.applySize();
      }
      if (e.target.closest('.btn-recalculate')) {
        this.reset();
      }
      if (e.target.closest('.alt-size')) {
        const altSize = e.target.closest('.alt-size');
        this.applySpecificSize(altSize.dataset.size);
      }
    });
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.removeAttribute('open');
    document.body.classList.remove('size-calculator-open');
  }

  selectOption(card) {
    const container = card.parentElement;
    const input = card.querySelector('input');
    const step = card.closest('.size-calculator-step');
    const stepNum = parseInt(step.dataset.step);

    // Remove selection from siblings
    container.querySelectorAll('.option-card').forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');
    input.checked = true;

    // Store selection
    if (stepNum === 1) this.selections.fitType = input.value;
    if (stepNum === 2) this.selections.physique = input.value;
    if (stepNum === 3) this.selections.preference = input.value;

    this.updateUI();

    // Auto-advance after short delay
    setTimeout(() => {
      if (this.currentStep < this.totalSteps) {
        this.nextStep();
      }
    }, 300);
  }

  selectSize(sizeOption) {
    const container = sizeOption.parentElement;
    const input = sizeOption.querySelector('input');

    container.querySelectorAll('.size-option').forEach(s => s.classList.remove('selected'));
    sizeOption.classList.add('selected');
    input.checked = true;

    this.selections.currentSize = input.value;
    this.updateUI();
  }

  nextStep() {
    if (this.currentStep < this.totalSteps && this.canProceed()) {
      this.currentStep++;
      this.updateUI();
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.updateUI();
    }
  }

  canProceed() {
    switch (this.currentStep) {
      case 1: return !!this.selections.fitType;
      case 2: return !!this.selections.physique;
      case 3: return !!this.selections.preference;
      case 4: return !!this.selections.currentSize;
      default: return false;
    }
  }

  updateUI() {
    // Update progress indicators
    this.progressSteps?.forEach((step, index) => {
      step.classList.remove('active', 'completed');
      if (index + 1 === this.currentStep) {
        step.classList.add('active');
      } else if (index + 1 < this.currentStep) {
        step.classList.add('completed');
      }
    });

    // Show current step
    this.steps?.forEach((step, index) => {
      step.classList.toggle('active', index + 1 === this.currentStep);
    });

    // Update navigation buttons
    if (this.backBtn) {
      this.backBtn.style.display = this.currentStep > 1 ? 'flex' : 'none';
    }

    if (this.nextBtn) {
      this.nextBtn.style.display = this.currentStep < this.totalSteps ? 'flex' : 'none';
      this.nextBtn.disabled = !this.canProceed();
    }

    if (this.calculateBtn) {
      this.calculateBtn.style.display = this.currentStep === this.totalSteps ? 'flex' : 'none';
      this.calculateBtn.disabled = !this.selections.currentSize;
    }
  }

  calculateSize() {
    const { fitType, physique, preference, currentSize } = this.selections;

    // Map input size to output size index
    // 2XL users are treated as XL for calculation purposes
    let baseSize = currentSize === '2XL' ? 'XL' : currentSize;
    const currentIndex = this.outputSizes.indexOf(baseSize);

    if (currentIndex === -1) return;

    let adjustment = 0;
    let explanation = '';

    // Base logic from fit type and physique
    // Fit Type Impact:
    // - slim: tends to run small, may need to size up
    // - regular: true to size
    // - oversized: intended to be loose, may size down

    // Physique Impact:
    // - cutting: losing mass, could size down or stay
    // - maintaining: stable, stay at current
    // - bulking: gaining mass, may need to size up

    // Combined logic matrix
    if (fitType === 'slim') {
      if (physique === 'cutting') {
        adjustment = 0;
        explanation = 'Slim fit during cutting phase - your current size should work well as you lean out.';
      } else if (physique === 'maintaining') {
        adjustment = 0;
        explanation = 'Slim fit while maintaining - stick with your usual size for a fitted look.';
      } else if (physique === 'bulking') {
        adjustment = 1;
        explanation = 'Slim fit during bulking - size up one to accommodate muscle growth.';
      }
    } else if (fitType === 'regular') {
      if (physique === 'cutting') {
        adjustment = 0;
        explanation = 'Regular fit during cutting - your current size offers versatile comfort.';
      } else if (physique === 'maintaining') {
        adjustment = 0;
        explanation = 'Regular fit while maintaining - your standard size is perfect.';
      } else if (physique === 'bulking') {
        adjustment = 0;
        explanation = 'Regular fit during bulking - regular cut provides room for growth.';
      }
    } else if (fitType === 'oversized') {
      if (physique === 'cutting') {
        adjustment = -1;
        explanation = 'Oversized fit during cutting - size down for that perfect relaxed look.';
      } else if (physique === 'maintaining') {
        adjustment = 0;
        explanation = 'Oversized fit while maintaining - your size gives the ideal loose silhouette.';
      } else if (physique === 'bulking') {
        adjustment = 0;
        explanation = 'Oversized fit during bulking - current size maintains the relaxed vibe.';
      }
    }

    // Preference adjustment
    if (preference === 'tight') {
      adjustment -= 1;
      explanation += ' Going tighter as you prefer.';
    } else if (preference === 'loose') {
      adjustment += 1;
      explanation += ' Added room for your comfort preference.';
    }

    // Calculate recommended size - clamp to valid output sizes (S, M, L, XL only)
    let recommendedIndex = currentIndex + adjustment;
    recommendedIndex = Math.max(0, Math.min(this.outputSizes.length - 1, recommendedIndex));

    this.recommendedSize = this.outputSizes[recommendedIndex];
    this.explanation = explanation;

    this.showLoader();
  }

  showLoader() {
    // Hide steps and nav
    this.steps.forEach(step => step.classList.remove('active'));
    this.navBtns.style.display = 'none';
    this.progressSteps.forEach(step => step.classList.add('completed'));

    // Show loading state
    const loadingState = this.modal.querySelector('.size-calculator-loading');
    if (loadingState) {
      loadingState.classList.add('active');
    }

    // After 1.5 seconds, hide loader and show result
    setTimeout(() => {
      if (loadingState) {
        loadingState.classList.remove('active');
      }
      this.showResult();
    }, 1500);
  }

  showResult() {
    // Hide steps and nav
    this.steps.forEach(step => step.classList.remove('active'));
    this.navBtns.style.display = 'none';
    this.progressSteps.forEach(step => step.classList.add('completed'));

    // Show result
    this.resultSection.classList.add('active');

    // Update result content
    const sizeDisplay = this.resultSection.querySelector('.recommended-size span');
    const explanationEl = this.resultSection.querySelector('.result-explanation');
    const summaryItems = this.resultSection.querySelectorAll('.summary-value');
    const altSizesContainer = this.resultSection.querySelector('.alt-sizes');

    if (sizeDisplay) sizeDisplay.textContent = this.recommendedSize;
    if (explanationEl) explanationEl.textContent = this.explanation;

    // Update summary
    if (summaryItems.length >= 4) {
      summaryItems[0].textContent = this.formatSelection(this.selections.fitType);
      summaryItems[1].textContent = this.formatSelection(this.selections.physique);
      summaryItems[2].textContent = this.formatSelection(this.selections.preference);
      summaryItems[3].textContent = this.selections.currentSize;
    }

    // Show alternative sizes (only within S, M, L, XL)
    if (altSizesContainer) {
      const currentIndex = this.outputSizes.indexOf(this.recommendedSize);
      let altHtml = '';

      // Size down option
      if (currentIndex > 0) {
        const sizeDown = this.outputSizes[currentIndex - 1];
        altHtml += `
          <div class="alt-size" data-size="${sizeDown}" tabindex="0">
            <span class="alt-size-label">${sizeDown}</span>
            <span class="alt-size-desc">Tighter fit</span>
          </div>
        `;
      }

      // Size up option
      if (currentIndex < this.outputSizes.length - 1) {
        const sizeUp = this.outputSizes[currentIndex + 1];
        altHtml += `
          <div class="alt-size" data-size="${sizeUp}" tabindex="0">
            <span class="alt-size-label">${sizeUp}</span>
            <span class="alt-size-desc">Roomier fit</span>
          </div>
        `;
      }

      altSizesContainer.innerHTML = altHtml;
    }
  }

  formatSelection(value) {
    const labels = {
      slim: 'Slim Fit',
      regular: 'Regular Fit',
      oversized: 'Oversized',
      cutting: 'Cutting/Leaning',
      maintaining: 'Maintaining',
      bulking: 'Bulking/Building',
      tight: 'Tight',
      comfortable: 'Comfortable',
      loose: 'Loose'
    };
    return labels[value] || value;
  }

  applySize() {
    this.applySpecificSize(this.recommendedSize);
  }

  applySpecificSize(size) {
    // Close modal first for better UX
    this.closeModal();

    let found = false;

    // Method 1: Try clicking the variant input directly (check input value attribute)
    const variantSelectors = document.querySelectorAll('variant-selects, variant-radios');

    variantSelectors.forEach(selector => {
      // For radio button inputs (pill type)
      const sizeInputs = selector.querySelectorAll('input[type="radio"]');

      sizeInputs.forEach(input => {
        const inputValue = input.value?.trim().toUpperCase();

        if (inputValue === size.toUpperCase()) {
          // Click the input
          input.click();

          // Also dispatch change event to ensure Shopify picks it up
          input.dispatchEvent(new Event('change', { bubbles: true }));
          input.dispatchEvent(new Event('input', { bubbles: true }));

          found = true;
        }
      });

      // For select dropdowns
      const selectInputs = selector.querySelectorAll('select');
      selectInputs.forEach(select => {
        const options = select.querySelectorAll('option');
        options.forEach(option => {
          if (option.value?.trim().toUpperCase() === size.toUpperCase()) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            found = true;
          }
        });
      });
    });

    // Method 2: If direct click didn't work, use URL parameters to reload with size selected
    if (!found) {
      this.showToast(`Selecting size ${size}...`);
      const url = new URL(window.location.href);
      url.searchParams.set('Size', size);
      window.location.href = url.toString();
    } else {
      // Show feedback
      this.showToast(`Size ${size} selected!`);
    }
  }

  showToast(message) {
    // Create toast notification
    const toast = document.createElement('div');
    toast.className = 'size-calculator-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
      color: #fff;
      padding: 1rem 2rem;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 600;
      z-index: 10000;
      animation: toastIn 0.3s ease, toastOut 0.3s ease 2s forwards;
    `;

    // Add animation styles if not present
    if (!document.querySelector('#size-calc-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'size-calc-toast-styles';
      style.textContent = `
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes toastOut {
          from { opacity: 1; transform: translateX(-50%) translateY(0); }
          to { opacity: 0; transform: translateX(-50%) translateY(-20px); }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(toast);

    setTimeout(() => toast.remove(), 2500);
  }

  reset() {
    this.currentStep = 1;
    this.selections = {
      fitType: null,
      physique: null,
      preference: null,
      currentSize: null
    };
    this.recommendedSize = null;
    this.explanation = '';

    // Reset UI
    if (this.modal) {
      this.modal.querySelectorAll('.option-card, .size-option').forEach(el => {
        el.classList.remove('selected');
      });
      this.modal.querySelectorAll('input[type="radio"]').forEach(input => {
        input.checked = false;
      });
    }

    this.resultSection.classList.remove('active');
    this.navBtns.style.display = 'flex';

    this.updateUI();
  }
}

// Register custom element
if (!customElements.get('size-calculator')) {
  customElements.define('size-calculator', SizeCalculator);
}

